import type { JSX } from 'react';
import type { ArticlePreview, ArticleResponseChildPost, INativo } from 'interfaces/content/articles/Articles';
import ArticleCard from 'components/Globals/ArticleCard';
import NativoPlaceholder from 'components/Nativo/NativoPlaceholder';
import type { Hub } from 'interfaces/Home';

type GetRowNumberFn = (index: number, isDesktop: boolean, isMobile: boolean) => number;

export const getRowNumber: GetRowNumberFn = (index: number, isDesktop: boolean, isMobile: boolean): number => {
  if (isDesktop) return Math.floor(index / 3);
  if (isMobile) return index;
  return Math.floor(index / 2);
};

export const generateNativoContent = (
  hubPosts: ArticlePreview[],
  isMicrosite: boolean,
  name: string,
  isDesktop: boolean,
  isMobile: boolean,
) => {
  const NativoHubPosts: JSX.Element[] = [];
  hubPosts?.forEach((post: ArticlePreview, index: number) => {
    const NATIVO_INDEXES_LIST = [1, 6, 11];
    if (NATIVO_INDEXES_LIST.indexOf(index) !== -1) {
      NativoHubPosts.push(
        <NativoPlaceholder
          key={`nativo-well-${index}`}
          className={`nativo-well-${NATIVO_INDEXES_LIST.indexOf(index) + 1}`}
        />,
      );
    }

    NativoHubPosts.push(
      <ArticleCard
        key={`article-card-${index}`}
        article={post}
        layoutVariant='hero'
        dataVariant={isMicrosite ? 'hub-microsite' : 'hub'}
        eyebrowType='red'
        titleSize={{ default: 'level3' }}
        priority={isMobile && index === 0}
        trackingData={{
          contentPlacementCD: `${name} grid - row ${getRowNumber(index, isDesktop, isMobile) + 1}`,
          eventAction: 'title click',
          eventCategory: 'navigation',
          eventLabel: post.title,
        }}
      />,
    );
  });
  return NativoHubPosts;
};

export const generateNativoLatest = (hubPosts: ArticlePreview[], isMdMin: boolean) => {
  const NativoHubPosts: JSX.Element[] = [];
  const NATIVO_INDEXES_LIST = [3];

  hubPosts?.forEach((post: ArticlePreview, index: number) => {
    if (NATIVO_INDEXES_LIST.indexOf(index) !== -1) {
      NativoHubPosts.push(
        <ul className={`nativo-section-${[3].indexOf(index) + 1}`}>
          <li>{!isMdMin && <NativoPlaceholder />}</li>
        </ul>,
      );
    }

    NativoHubPosts.push(
      <ArticleCard
        article={post}
        layoutVariant={`${isMdMin ? 'card-xxs' : 'card-xl'}`}
        className='sections-small-card no-eyebrow'
        titleSize={{ default: 'level6', smMin: 'level6' }}
        trackingData={{
          contentPlacementCD: 'homepage sections nativo ad',
          eventAction: 'title click',
          eventCategory: 'navigation',
          eventLabel: post.title,
        }}
      />,
    );
  });

  return NativoHubPosts;
};

// Helper to get Nativo children category
export const getNativoChildrenCategory = (childData: Hub['children'], hasNativo: boolean): INativo[] => {
  return childData.map((child, idx) => {
    const NativoCategory: INativo = {
      link: child.link,
      name: child.name,
      nativo: {
        nativo_enable: child.nativo?.nativo_enable as boolean,
      },
      posts: [],
      slug: child.slug,
    };

    const NativoChildrenPosts: JSX.Element[] = [];

    if (child.posts && child.posts.nodes.length > 0) {
      child.posts.nodes.forEach((postEntity: ArticleResponseChildPost | ArticlePreview, index: number) => {
        const post: ArticlePreview = postEntity as ArticlePreview;

        if (index === 2 && hasNativo) {
          NativoChildrenPosts.push(
            <ul
              className={`nativo-section-${idx + 2}`}
              key={`nativo-ul-${post.databaseId || index}`}
            >
              <li>
                <ArticleCard
                  article={post}
                  layoutVariant='card-simple'
                  className='no-eyebrow'
                  titleSize={{ default: 'level6', smMin: 'level6' }}
                  trackingData={{
                    contentPlacementCD: 'homepage sections nativo ad',
                    eventAction: 'title click',
                    eventCategory: 'navigation',
                    eventLabel: post.title,
                  }}
                />
              </li>
            </ul>,
          );
        } else if (index === 0) {
          NativoChildrenPosts.push(
            <ArticleCard
              key={`nativo-xl-${post.databaseId || index}`}
              article={post}
              layoutVariant='card-xl'
              className='no-eyebrow'
              titleSize={{ default: 'level6', smMin: 'level3' }}
              trackingData={{
                contentPlacementCD: 'homepage sections nativo ad',
                eventAction: 'title click',
                eventCategory: 'navigation',
                eventLabel: post.title,
              }}
            />,
          );
        } else {
          NativoChildrenPosts.push(
            <ArticleCard
              key={`nativo-simple-${post.databaseId || index}`}
              article={post}
              layoutVariant='card-simple'
              className='no-eyebrow'
              titleSize={{ default: 'level6', smMin: 'level6' }}
              trackingData={{
                contentPlacementCD: 'homepage sections nativo ad',
                eventAction: 'title click',
                eventCategory: 'navigation',
                eventLabel: post.title,
              }}
            />,
          );
        }
      });
    }

    NativoCategory.posts = NativoChildrenPosts;
    return NativoCategory;
  });
};
export default generateNativoContent;
